/** The after-action report's types and presentation helpers. Pure.
 *
 * **`buildBattleReport` below is not called by anything, and adding a
 * `StackStore.startCount` column would not change that.** The header here used
 * to say a casualty list "cannot be derived from the final battle state alone"
 * without that column, which read as a live constraint and is not one: the
 * blocker is that there IS no final battle state to derive from.
 * `resolveBattleOutcome` clears `s.battle` in the same command that lands the
 * last blow, so no host can ever hold the battle as it ended - the state before
 * is one blow stale and the state after has no battle at all.
 *
 * The live report is built in `AdventureShell.noteBattle`/`reportFrom` from the
 * pair of army snapshots either side of the fold, which is exact for exactly
 * the reason a stack-store column would have been: `foldSide` writes survivors
 * into `state.armies` before the battle is cleared. It produces the same
 * `SideReport` and `CasualtyLine` this file declares, with real losses.
 *
 * So what stays live here is the vocabulary - `BattleReport`, `SideReport`,
 * `CasualtyLine` - and `outcomeHeadline`, which `BattleResult.tsx` imports.
 * `buildBattleReport` and its `sideReport` helper are kept only because they
 * are exported through `screens/index.ts` and removing a public export is a
 * separate decision from correcting a comment.
 *
 * Experience and artifacts come from the event stream, never from a rule
 * module: `HeroGainedExperience` and `ArtifactGained` are what actually
 * happened, and the report is a transcript, not a second opinion (I10).
 */

import type {
  ArtifactDefId, BattleState, ContentRegistry, CreatureDefId, GameEvent, HeroId,
} from "@game/core";
import { F_ALIVE } from "@game/core";

export interface CasualtyLine {
  readonly creature: CreatureDefId;
  readonly name: string;
  readonly started: number;
  readonly survived: number;
  readonly lost: number;
}

export interface SideReport {
  readonly side: 0 | 1;
  readonly hero: HeroId | null;
  readonly lines: readonly CasualtyLine[];
  readonly startedTotal: number;
  readonly survivedTotal: number;
  readonly lostTotal: number;
  /** Sum of `aiValue x lost`, the same currency the classic pays experience in. */
  readonly valueLost: number;
}

export interface BattleReport {
  readonly winner: 0 | 1 | null;
  readonly kind: string;
  readonly rounds: number;
  readonly attacker: SideReport;
  readonly defender: SideReport;
  readonly experience: readonly { readonly hero: HeroId; readonly amount: number }[];
  readonly artifacts: readonly { readonly hero: HeroId; readonly artifact: ArtifactDefId }[];
  readonly levelUps: readonly { readonly hero: HeroId; readonly level: number }[];
}

function sideReport(
  content: ContentRegistry,
  battle: BattleState,
  startCounts: readonly number[],
  side: 0 | 1,
): SideReport {
  const s = battle.stacks;
  const merged = new Map<string, { started: number; survived: number }>();
  const order: CreatureDefId[] = [];

  for (let i = 0; i < s.n; i++) {
    if (s.side[i] !== side) continue;
    const def = s.def[i];
    if (def === undefined) continue;
    const alive = ((s.flags[i] ?? 0) & F_ALIVE) !== 0;
    const survived = alive ? (s.count[i] ?? 0) : 0;
    // A missing snapshot means "we never knew"; survivors are still the truth.
    const started = startCounts[i] ?? survived;
    const key = def as string;
    const row = merged.get(key);
    if (row === undefined) { merged.set(key, { started, survived }); order.push(def); }
    else { row.started += started; row.survived += survived; }
  }

  const lines: CasualtyLine[] = [];
  let startedTotal = 0;
  let survivedTotal = 0;
  let valueLost = 0;
  for (const def of order) {
    const row = merged.get(def as string);
    if (row === undefined) continue;
    const cdef = content.creatures.get(def);
    const lost = row.started - row.survived;
    lines.push({
      creature: def,
      name: cdef?.namePlural ?? (def as string),
      started: row.started,
      survived: row.survived,
      lost: lost < 0 ? 0 : lost,
    });
    startedTotal += row.started;
    survivedTotal += row.survived;
    valueLost += (lost < 0 ? 0 : lost) * (cdef?.aiValue ?? 0);
  }

  return {
    side,
    hero: battle.heroes[side]?.id ?? null,
    lines,
    startedTotal,
    survivedTotal,
    lostTotal: startedTotal - survivedTotal,
    valueLost,
  };
}

export function buildBattleReport(
  content: ContentRegistry,
  battle: BattleState,
  startCounts: readonly number[],
  events: readonly GameEvent[],
): BattleReport {
  const experience: { hero: HeroId; amount: number }[] = [];
  const artifacts: { hero: HeroId; artifact: ArtifactDefId }[] = [];
  const levelUps: { hero: HeroId; level: number }[] = [];
  for (const e of events) {
    if (e.type === "HeroGainedExperience") experience.push({ hero: e.hero, amount: e.amount });
    else if (e.type === "ArtifactGained") artifacts.push({ hero: e.hero, artifact: e.artifact });
    else if (e.type === "HeroLeveledUp") levelUps.push({ hero: e.hero, level: e.level });
  }

  const outcome = battle.outcome;
  return {
    winner: outcome.kind === "victory" || outcome.kind === "retreat" || outcome.kind === "surrender"
      ? (outcome.kind === "victory" ? outcome.side : (outcome.side === 0 ? 1 : 0))
      : null,
    kind: outcome.kind,
    rounds: battle.round,
    attacker: sideReport(content, battle, startCounts, 0),
    defender: sideReport(content, battle, startCounts, 1),
    experience,
    artifacts,
    levelUps,
  };
}

/** Headline wording for the outcome, from the winner's point of view. */
export function outcomeHeadline(report: BattleReport, viewerSide: 0 | 1 | null): string {
  if (report.kind === "draw") return "Mutual Destruction";
  if (report.winner === null) return "The Battle Continues";
  const won = viewerSide !== null && report.winner === viewerSide;
  if (report.kind === "retreat") return won ? "The Enemy Fled" : "You Fled the Field";
  if (report.kind === "surrender") return won ? "The Enemy Surrendered" : "You Surrendered";
  return won ? "Victory" : "Defeat";
}
